import styles from './Tab.module.scss';
import AvatarWithMarketplace from '@components/AvatarWithMarketplace';
import { Badge, BadgeType } from '@components/new/Badge';
import { Tooltip } from '@components/new/Tooltip';
import InfoIcon from '@assets/icons/info-grey-18.svg';
import Box from '@components/Box';
import CardParcelModalWindow from '../modals/CardParcelModalWindow/CardParcelModalWindow';
import cn from 'classnames';
import DataTable, { DataViewHandle } from '@containers/DataTable';
import { FilterModel } from '@hooks/useFiltersSearchParams';
import { Filters, FilterTypes } from '@containers/Filters';
import { formatDate, formatSumWithCurrency } from '@utils/formatValues';
import { getCurrentLanguage } from '@utils/lang';
import { getReturns, TReturnsParcel } from '@api/apiClient/returns';
import { IReturnsTabProps } from '../types';
import { Modal } from '@components/Modal';
import { Row, Column } from '@components/Grid';
import { sortByPrice, sortByStores } from '@utils/sorting';
import { TableColumn, TableColumnType, TableRowType } from '@components/Table/TableTypes.ts';
import { Text, TextColor, TextSize } from '@components/Text';
import TrackingNumber from "@components/TrackingNumber";
import { useProfile } from '@contexts/ProfileContext';
import { useState, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const ReturnsTab: React.FC<IReturnsTabProps> = (props: IReturnsTabProps): React.ReactElement => {
  const {
    isFilterOpen,
    setIsFilterOpen,
    // onUpdate
  } = props;

  const [columnsSort, setColumnsSort] = useState<string[]>([]);
  const [innerFilters, setInnerFilters] = useState<FilterModel[]>([]);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [isOpenParcelWindow, setIsOpenParcelWindow] = useState(false);
  const [parcel, setParcel] = useState<TReturnsParcel | undefined>(undefined);

  const dataRef = useRef<DataViewHandle>(null);

  const currentLanguage = getCurrentLanguage();
  const { account } = useProfile();
  const { t } = useTranslation();

  const variableCurrency: string = account?.region === 'TR' ? '$' : '¥';

  // const refreshData = () => {
  //   if (dataRef.current?.refresh) {
  //     dataRef.current.refresh();
  //   }
  //   onUpdate();
  // };

  const onCloseParcel = () => {
    setIsOpenParcelWindow(false);
  };

  const handlerSortByPrice = (position: number, value: string, filters: FilterModel[]): void => {
    sortByPrice({ columnsSort, filters, position, setColumnsSort, setInnerFilters, value });
  };

  const handlerSortByStores = (position: number, value: string, filters: FilterModel[]): void => {
    sortByStores({ columnsSort, filters, position, setColumnsSort, setInnerFilters, value });
  };

  const columns: TableColumn<TReturnsParcel>[] = useMemo(() => [
    {
      header: t("common.trackingNumber"),
      accessor: (row: TableRowType<TReturnsParcel>) => (
        <TrackingNumber
          externalId={row.data?.trackingNumber}
          trackNumber={row.data?.directStreamExternalId}
          onClick={() => {
            setIsOpenParcelWindow(true);
            setParcel(row.data);
          }}
          allowEqualNumbers
        />
      ),
      fixWidth: true,
      noOverflow: true,
      width: "200px"
    },
    {
      header: t('returnsPage.returnsTab.name'),
      accessor: (row: TableRowType<TReturnsParcel>) => (
          <a
              className={styles.link}
              href={row?.data?.url}
              rel="noreferrer"
              target="_blank"
          >
            {row?.data?.title}
          </a>
      ),
      width: '235px'
    },
    {
      header: t('returnsPage.returnsTab.priceOfProduct'),
      accessor: (row: TableRowType<TReturnsParcel>) => (
          <div className={styles.price}>
            <Text
                color={TextColor.PRIMARY}
                size={TextSize.S}
            >
              {formatSumWithCurrency(row.data.priceRub, '₽')}
            </Text>
            <Text
                color={TextColor.DARK_MUTED}
                size={TextSize.XS}
            >
              {formatSumWithCurrency(row.data.priceCny, variableCurrency)}
            </Text>
          </div>
      ),
      alignment: 'right',
      noOverflow: true,
      sortable: true,
      sortableFn: handlerSortByPrice,
      width: '100px'
    },
    {
      header: t('returnsPage.returnsTab.store'),
      accessor: (row: TableRowType<TReturnsParcel>) => (
        <AvatarWithMarketplace
          id={row.data.seller.id}
          name={row.data.seller.name}
          marketplace={row.data.marketplaceType}
        />
      ),
      alignment: 'center',
      noOverflow: true,
      sortable: true,
      sortableFn: handlerSortByStores
    },
    {
      header: t('returnsPage.returnsTab.returnType'),
      accessor: (row: TableRowType<TReturnsParcel>) => {
        const customerReturnStatus = row.data.customerReturnStatus;
        if (!customerReturnStatus) return null;
        const isDeliverToLocal = customerReturnStatus.startsWith('DeliverToLocal');
        return (
            <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
              <Badge type={isDeliverToLocal ? BadgeType.SECONDARY : BadgeType.PRIMARY}>
                {t(`returnsPage.returnsTab.statusGroups.${isDeliverToLocal ? 'deliverToLocal' : 'reexport'}`)}
              </Badge>
            </div>
        );
      },
      alignment: 'left',
      width: "160px",
      fixWidth: true,
      noOverflow: true,
    },
    {
      header: t('returnsPage.returnsTab.status'),
      accessor: (row: TableRowType<TReturnsParcel>) => (
        <span className={styles.row}>
          <Text
            color={(row.data.status === "ReexportNotDelivered") ? TextColor.ERROR : TextColor.PRIMARY}
            size={TextSize.S}
          >
            {t(`returnsPage.returnsTab.statuses.${row.data.status}`)}
          </Text>
          {row.data.status === "ReexportNotDelivered" ? (
            <Tooltip
              action={<img alt="Info" src={InfoIcon} />}
              text={t('returnsPage.returnsTab.notDeliveredTooltip')}
              position="bottom"
              whiteSpace="pre-wrap"
            />
          ) : null}
        </span>
      ),
      width: "120px",
      fixWidth: true,
      noOverflow: true,
    },
    {
      header: t('returnsPage.returnsTab.date'),
      accessor: (row: TableRowType<TReturnsParcel>) => formatDate(row.data.reexportVoteDate, 'dd.MM.yy'),
      alignment: 'right',
      type: TableColumnType.TERTIARY
    }
  ], [currentLanguage, columnsSort]);

  useEffect(() => {
    const columnsSort: string[] = columns.map((item: TableColumn<TReturnsParcel>) => item.sortable ? 'asc' : '');
    setColumnsSort(columnsSort);
  }, []);

  return (
      <div>
        <Box pb={5}>
          <Text
              color={TextColor.PRIMARY}
              size={TextSize.S}
          >
            {t('returnsPage.returnsTab.info_1')}
          </Text>
        </Box>
        <Box
            pb={5}
            className={cn(!isFilterOpen && styles.hidden)}
        >
          <Column phone={12}>
            <Row
                justify="right"
                gap={5}
            >
              <Filters
                  id="returns-returns"
                  openFilters={() => setIsFilterOpen(true)}
                  items={[
                    {
                      name: 'trackNumbers',
                      type: FilterTypes.MULTI_SEARCH,
                      placeholder: t('filters.searchByTrackNumber')
                    },
                    {
                      name: 'sellerIds',
                      type: FilterTypes.SELLER
                    },
                    // {
                    //   name: "marketplaceType",
                    //   type: FilterTypes.MARKETPLACE,
                    // },
                    {
                      name: 'dateRange',
                      type: FilterTypes.DATE_RANGE,
                      placeholder: t('returnsPage.returnsTab.date'),
                      fieldNames: ['startDate', 'endDate']
                    }
                  ]}
                  onChange={(v, isReady) => {
                    if (isReady) {
                      setIsInitialized(true);
                    }
                    setInnerFilters(v);
                  }}
                  values={innerFilters}
                  asPanel
              />
            </Row>
          </Column>
        </Box>
        <DataTable<TReturnsParcel>
            columns={columns}
            columnsSort={columnsSort}
            dataRef={dataRef}
            defaultPageSize={10}
            filters={innerFilters}
            getData={getReturns}
            notFound={t('returnsPage.returnsTab.noParcels')}
            pending={!isInitialized}
            queryKey="returnsParcels"
        />
        <Modal
            bodyWithoutIndentations
            isOpen={isOpenParcelWindow}
            onClickOverlay={onCloseParcel}
            onClose={onCloseParcel}
            width={900}
        >
          <CardParcelModalWindow
              callFrom="returnsTab"
              handlerSetIsOpenParcelWindow={setIsOpenParcelWindow}
              parcel={parcel!}
          />
        </Modal>
      </div>
  );
};

export default ReturnsTab;
